import { useTellescopeMutation } from "@tellescope/tanstack-query";
import type { ObservationComponent } from "@tellescope/types-models";
import { useEnduserStore } from "../store/hooks";

export interface LogNutritionEntryParams {
  /** Total/primary value for this entry, e.g. total calories for a meal, or the weigh-in itself. */
  measurement: { value: number; unit: string };
  /** Macro/ingredient breakdown — omit for simple single-value entries like weight or water. */
  components?: ObservationComponent[];
  /** Free-text observation type convention, e.g. "weight" | "food" | "water". */
  type: string;
  timestamp?: Date;
}

/**
 * Logs a new weight/macro/water entry as an EnduserObservation.
 * Always sets dontTrigger — no vital_configurations exist for these units today, and frequent
 * logging shouldn't risk firing automations designed for a clinical vitals cadence.
 */
export const useLogNutritionEntry = () => {
  const session = useEnduserStore((state) => state.session);
  const enduserId = useEnduserStore((state) => state.currentEnduser?.id);

  const mutation = useTellescopeMutation({
    model: "enduser_observations",
    method: "createOne",
    session: session ?? undefined,
    autoCacheUpdateStrategy: "invalidate",
  });

  const buildPayload = ({ measurement, components, type, timestamp }: LogNutritionEntryParams) => ({
    enduserId: enduserId ?? "",
    status: "final" as const,
    category: "vital-signs" as const,
    source: "tellescope-portal",
    dontTrigger: true,
    type,
    measurement,
    components,
    timestamp: timestamp ?? new Date(),
  });

  const logEntry = (params: LogNutritionEntryParams) => mutation.mutate(buildPayload(params));
  const logEntryAsync = (params: LogNutritionEntryParams) => mutation.mutateAsync(buildPayload(params));

  return {
    ...mutation,
    logEntry,
    logEntryAsync,
  };
};
